import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utils'
import type { PhotoEffectDefinition } from '@/lib/photo-effects/types/photo-effect-definition'
import {
  formatPhotoEffectRangeHint,
  formatPhotoEffectSliderAriaValueText,
  photoEffectRowLabelId,
  photoEffectRowLiveValueId,
  photoEffectRowRangeHintId,
} from '@/lib/photo-effects/photo-effect-a11y'
import {
  formatPhotoEffectValue,
  parsePhotoEffectInput,
} from '@/lib/photo-effects/photo-effect-value-input'
import { PhotoEffectEyeButton } from '@/components/app/photos/photo-effects/components/photo-effect-eye-button'
import { PhotoEffectSlider } from '@/components/app/photos/photo-effects/components/photo-effect-slider'
import {
  FIGMA_INPUT_SHADOW,
  FIGMA_TEXT_FOREGROUND,
  FIGMA_TEXT_MUTED,
  FIGMA_TEXT_PRIMARY,
  PEZ_ADJUSTABLE_ROW_GRID,
  PEZ_ADJUSTABLE_ROW_HEIGHT,
  PEZ_ADJUSTABLE_ROW_PADDING_Y,
  PEZ_EFFECTS_LIST_WIDTH,
  PEZ_EYE_BUTTON_SIZE,
  PEZ_INPUT_WIDTH,
  PEZ_ROW_GAP,
  PEZ_ROW_PADDING_X,
} from '@/components/app/photos/photo-effects/constants/photo-effect-row-layout'

export type PhotoEffectAdjustableRowProps = {
  def: PhotoEffectDefinition
  active: boolean
  value: number
  onToggle: () => void
  onValueChange: (value: number) => void
  className?: string
}

/**
 * Adjustable effect row (WMPCM-9477 / WMPCM-9494 / Figma PEZ node 114-8833).
 * Tab order: eye toggle → slider → number input.
 */
export function PhotoEffectAdjustableRow({
  def,
  active,
  value,
  onToggle,
  onValueChange,
  className,
}: PhotoEffectAdjustableRowProps) {
  const { label, kind, min = 0, max = 100 } = def
  const sliderMode = kind === 'symmetric' ? 'symmetric' : 'percent'
  const labelId = photoEffectRowLabelId(def.id)
  const rangeHintId = photoEffectRowRangeHintId(def.id)
  const liveValueId = photoEffectRowLiveValueId(def.id)
  const sliderValueText = formatPhotoEffectSliderAriaValueText(sliderMode, value)

  const [inputValue, setInputValue] = useState(() => formatPhotoEffectValue(def.id, value))
  const [isSliderFocused, setIsSliderFocused] = useState(false)
  const isFocusedRef = useRef(false)
  const lastValidRef = useRef(value)

  useEffect(() => {
    lastValidRef.current = value

    if (!isFocusedRef.current) {
      setInputValue(formatPhotoEffectValue(def.id, value))
    }
  }, [def.id, value])

  const commitValue = (next: number) => {
    lastValidRef.current = next
    setInputValue(formatPhotoEffectValue(def.id, next))
    onValueChange(next)
  }

  const handleInputChange = (raw: string) => {
    setInputValue(raw)
    const parsed = parsePhotoEffectInput(def.id, raw)

    if (parsed != null) {
      commitValue(parsed)
    }
  }

  const handleInputBlur = () => {
    isFocusedRef.current = false
    const parsed = parsePhotoEffectInput(def.id, inputValue)

    if (parsed == null) {
      setInputValue(formatPhotoEffectValue(def.id, lastValidRef.current))
      return
    }

    commitValue(parsed)
  }

  const labelColor = active ? FIGMA_TEXT_PRIMARY : FIGMA_TEXT_FOREGROUND
  const inputColor = active ? FIGMA_TEXT_PRIMARY : FIGMA_TEXT_MUTED

  return (
    <div
      role="group"
      aria-label={label}
      className={cn('box-border shrink-0', className)}
      style={{ width: PEZ_EFFECTS_LIST_WIDTH }}
      data-photo-effect-adjustable-row
      data-photo-effect-id={def.id}
    >
      <div
        className="box-border"
        style={{
          height: PEZ_ADJUSTABLE_ROW_HEIGHT,
          padding: `${PEZ_ADJUSTABLE_ROW_PADDING_Y}px ${PEZ_ROW_PADDING_X}px`,
          display: 'grid',
          gridTemplateColumns: PEZ_ADJUSTABLE_ROW_GRID,
          columnGap: PEZ_ROW_GAP,
          alignItems: 'center',
        }}
      >
        <PhotoEffectEyeButton label={label} active={active} onToggle={onToggle} />

        <span
          id={labelId}
          className="block min-w-0 truncate text-sm font-medium leading-[14px]"
          style={{ textAlign: 'left', color: labelColor }}
          data-photo-effect-fx-label
        >
          {label}
        </span>

        <PhotoEffectSlider
          mode={sliderMode}
          value={value}
          min={min}
          max={max}
          active={active}
          onValueChange={commitValue}
          ariaLabelledBy={labelId}
          ariaValueText={sliderValueText}
          onSliderFocus={() => setIsSliderFocused(true)}
          onSliderBlur={() => setIsSliderFocused(false)}
          className="justify-self-stretch"
        />

        <input
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          aria-labelledby={labelId}
          aria-describedby={rangeHintId}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            isFocusedRef.current = true
          }}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur()
            }
          }}
          className="box-border justify-self-end rounded-lg border border-[#E5E5E5] bg-white p-0.5 text-center text-sm font-normal leading-5 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          style={{
            width: PEZ_INPUT_WIDTH,
            height: PEZ_EYE_BUTTON_SIZE,
            boxShadow: FIGMA_INPUT_SHADOW,
            color: inputColor,
          }}
        />
      </div>

      <span id={rangeHintId} className="sr-only">
        {formatPhotoEffectRangeHint(min, max)}
      </span>

      <span id={liveValueId} aria-live="polite" aria-atomic="true" className="sr-only">
        {isSliderFocused ? sliderValueText : ''}
      </span>
    </div>
  )
}
